import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, Globe, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message sent successfully! We will get back to you soon.');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      const subject = encodeURIComponent(`Inquiry from ${formData.name}`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\n\nMessage:\n${formData.message}`
      );
      window.location.href = `mailto:info@syrveo.com?subject=${subject}&body=${body}`;
      toast.info('Opening your email client...');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-[#0A1128] via-[#1E3A8A] to-[#0F172A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Get in <span className="text-[#10B981]">Touch</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to transform your payment infrastructure? Let's talk about your business needs.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <div className="space-y-8">
            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#10B981]/20 rounded-lg flex items-center justify-center">
                    <Globe className="text-[#10B981]" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Website</div>
                    <a href="https://syrveo.com" className="text-white hover:text-[#10B981] transition-colors duration-300 font-medium" data-testid="contact-website-link">
                      syrveo.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#10B981]/20 rounded-lg flex items-center justify-center">
                    <Mail className="text-[#10B981]" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Email</div>
                    <a href="mailto:info@syrveo.com" className="text-white hover:text-[#10B981] transition-colors duration-300 font-medium" data-testid="contact-email-link">
                      info@syrveo.com
                    </a>
                  </div>
                </div>

                

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#10B981]/20 rounded-lg flex items-center justify-center">
                    <MapPin className="text-[#10B981]" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Headquarters</div>
                    <div className="text-white font-medium">Vienna, Austria</div>
                                      </div>
                </div>
              </div>

              <a
                href="mailto:info@syrveo.com"
                className="mt-8 flex items-center justify-center space-x-2 w-full bg-[#10B981] hover:bg-[#059669] text-white px-6 py-4 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                data-testid="email-cta-button"
              >
                <Mail size={20} />
                <span>Email us directly</span>
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-md p-2 rounded-2xl border border-white/20 overflow-hidden">
              <iframe
                title="Syrveo Location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=16.3450%2C48.1980%2C16.3850%2C48.2200&layer=mapnik&marker=48.2092%2C16.3728"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '0.75rem' }}
                loading="lazy"
                data-testid="contact-map"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
            <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-[#10B981]"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-[#10B981]"
                  required
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <Label htmlFor="company" className="text-white mb-2 block">Company Name</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-[#10B981]"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-white mb-2 block">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your business needs..."
                  rows={5}
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-[#10B981]"
                  required
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-white px-8 py-6 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    Send Message
                    <Send className="ml-2" size={20} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
